class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.stickyClass = 'show';
    this.position = this.dataset.position || 'bottom';
    
    // Bind handlers once to preserve context
    this.onScrollHandler = this.handleScroll.bind(this);
    this.addToCartHandler = this.handleAddToCart.bind(this);
    this.variantChangeHandler = this.handleVariantChange.bind(this);
    this.quantityChangeHandler = this.handleQuantityChange.bind(this);
    // New: track observer and whether we're using it
    this.visibilityObserver = null;
    this.usesObserver = false;
    // New: only show after the main ATC has been in view at least once
    this.hasReachedMainButton = false;
  }

  connectedCallback() {
    // Add event listeners
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });
    
    // Button click handler
    const addToCartButton = this.querySelector('.product-form__submit');
    if (addToCartButton) {
      addToCartButton.addEventListener('click', this.addToCartHandler);
    }
    
    // Listen for custom event
    document.addEventListener('sticky-add-to-cart:click', this.addToCartHandler);
    
    // Set up variant listeners
    this.setupVariantObserver();
    this.setupVariantChangeListeners();
    
    // Set up quantity sync
    this.setupQuantitySync();
    
    // Initialize
    this.updateStickyBarVariant();
    // New: visibility observer that tracks main add-to-cart visibility
    this.setupVisibilityObserver();
    this.handleScroll();
  }

  disconnectedCallback() {
    // Remove scroll listener
    window.removeEventListener('scroll', this.onScrollHandler);
    
    // Remove button listener
    const addToCartButton = this.querySelector('.product-form__submit');
    if (addToCartButton) {
      addToCartButton.removeEventListener('click', this.addToCartHandler);
    }
    
    // Remove custom event listener
    document.removeEventListener('sticky-add-to-cart:click', this.addToCartHandler);
    
    // Remove variant listeners
    this.removeVariantListeners();
    
    // Remove quantity listeners
    this.removeQuantityListeners();
    
    // Disconnect observer
    if (this.variantObserver) {
      this.variantObserver.disconnect();
    }
    // New: disconnect visibility observer
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
      this.usesObserver = false;
    }
  }
  
  setupVariantObserver() {
    // Use MutationObserver to watch variant ID changes
    const mainForm = document.querySelector('product-form form') ||
                    document.querySelector('form[data-type="add-to-cart-form"]');
    
    if (!mainForm) return;
    
    const variantIdInput = mainForm.querySelector('[name="id"]');
    if (!variantIdInput) return;
    
    this.variantObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          this.updateStickyBarVariant();
          break;
        }
      }
    });
    
    this.variantObserver.observe(variantIdInput, { 
      attributes: true, 
      attributeFilter: ['value'] 
    });
  }

  setupVariantChangeListeners() {
    // Common selector for all variant inputs
    const variantInputSelectors = [
      '.product-form__input select',
      '.product-form__input input[type="radio"]',
      '.js-change-variant',
      '.swatch input',
      '.swatch-element',
      '.single-option-selector'
    ].join(', ');
    
    // Add listeners to all variant controls
    document.querySelectorAll(variantInputSelectors).forEach(el => {
      el.addEventListener('change', this.variantChangeHandler);
      
      // Also listen for clicks on radios and swatches
      if (el.type === 'radio' || el.classList.contains('swatch-element') || 
          el.classList.contains('js-change-variant')) {
        el.addEventListener('click', this.variantChangeHandler);
      }
    });
    
    // Listen for common variant change events
    ['variant:change', 'variant:changed', 'property_select:change'].forEach(eventName => {
      document.addEventListener(eventName, this.variantChangeHandler);
    });
    
    // Listen for form changes
    const productForm = document.querySelector('product-form') || 
                       document.querySelector('.product-form');
    if (productForm) {
      productForm.addEventListener('change', this.variantChangeHandler);
    }
  }
  
  removeVariantListeners() {
    // Remove all variant control listeners
    const variantInputSelectors = [
      '.product-form__input select',
      '.product-form__input input[type="radio"]',
      '.js-change-variant',
      '.swatch input',
      '.swatch-element',
      '.single-option-selector'
    ].join(', ');
    
    document.querySelectorAll(variantInputSelectors).forEach(el => {
      el.removeEventListener('change', this.variantChangeHandler);
      
      if (el.type === 'radio' || el.classList.contains('swatch-element') || 
          el.classList.contains('js-change-variant')) {
        el.removeEventListener('click', this.variantChangeHandler);
      }
    });
    
    // Remove event listeners
    ['variant:change', 'variant:changed', 'property_select:change'].forEach(eventName => {
      document.removeEventListener(eventName, this.variantChangeHandler);
    });
    
    // Remove form listeners
    const productForm = document.querySelector('product-form') || 
                       document.querySelector('.product-form');
    if (productForm) {
      productForm.removeEventListener('change', this.variantChangeHandler);
    }
  }
  
  setupQuantitySync() {
    const sectionId = this.dataset.sectionId;
    const mainQuantityInput = document.querySelector(`#Quantity-${sectionId}`);
    const stickyQuantityInput = this.querySelector('[data-sticky-quantity]');
    
    if (!mainQuantityInput || !stickyQuantityInput) return;
    
    // Set initial value and sync min/max attributes
    stickyQuantityInput.value = mainQuantityInput.value;
    this.syncQuantityAttributes();
    
    // Main quantity changes -> update sticky
    mainQuantityInput.addEventListener('change', this.quantityChangeHandler);
    
    // Also listen for attribute changes on main input (for order limit updates)
    this.mainInputObserver = new MutationObserver(() => {
      this.syncQuantityAttributes();
    });
    this.mainInputObserver.observe(mainQuantityInput, {
      attributes: true,
      attributeFilter: ['min', 'max', 'data-min', 'data-max', 'value']
    });
    
    // Sticky quantity changes -> update main
    stickyQuantityInput.addEventListener('change', () => {
      mainQuantityInput.value = stickyQuantityInput.value;
      mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    });
    
    // Handle quantity buttons
    this.querySelectorAll('.quantity__button').forEach(button => {
      button.addEventListener('click', () => {
        // Small delay to let the quantity input update
        setTimeout(() => {
          mainQuantityInput.value = stickyQuantityInput.value;
          mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }, 10);
      });
    });
  }

  /**
   * Syncs min/max attributes from main quantity input to sticky quantity input.
   * This ensures order limits are enforced in the sticky bar UI.
   */
  syncQuantityAttributes() {
    const sectionId = this.dataset.sectionId;
    const mainQuantityInput = document.querySelector(`#Quantity-${sectionId}`);
    const stickyQuantityInput = this.querySelector('[data-sticky-quantity]');
    
    if (!mainQuantityInput || !stickyQuantityInput) return;
    
    // Sync value
    stickyQuantityInput.value = mainQuantityInput.value;
    
    // Sync min
    const min = mainQuantityInput.getAttribute('min');
    if (min !== null) {
      stickyQuantityInput.setAttribute('min', min);
      stickyQuantityInput.setAttribute('data-min', min);
    }
    
    // Sync max (order limit aware)
    const max = mainQuantityInput.getAttribute('max');
    if (max !== null && max !== '') {
      stickyQuantityInput.setAttribute('max', max);
      stickyQuantityInput.setAttribute('data-max', max);
    } else {
      stickyQuantityInput.removeAttribute('max');
      stickyQuantityInput.removeAttribute('data-max');
    }
    
    // Trigger QuantityInput validation to update +/- button states
    const quantityInputEl = stickyQuantityInput.closest('quantity-input');
    if (quantityInputEl && typeof quantityInputEl.validateQtyRules === 'function') {
      quantityInputEl.validateQtyRules();
    }
  }
  
  removeQuantityListeners() {
    const sectionId = this.dataset.sectionId;
    const mainQuantityInput = document.querySelector(`#Quantity-${sectionId}`);
    const stickyQuantityInput = this.querySelector('[data-sticky-quantity]');
    
    if (mainQuantityInput) {
      mainQuantityInput.removeEventListener('change', this.quantityChangeHandler);
    }
    
    // Disconnect mutation observer for min/max sync
    if (this.mainInputObserver) {
      this.mainInputObserver.disconnect();
      this.mainInputObserver = null;
    }
    
    if (stickyQuantityInput) {
      const changeHandler = () => {};
      stickyQuantityInput.removeEventListener('change', changeHandler);
    }
    
    this.querySelectorAll('.quantity__button').forEach(button => {
      button.removeEventListener('click', () => {});
    });
  }

  handleQuantityChange(event) {
    const stickyQuantityInput = this.querySelector('[data-sticky-quantity]');
    if (stickyQuantityInput && event.target) {
      stickyQuantityInput.value = event.target.value;
      // Also sync attributes in case they changed (e.g., order limit recalculated)
      this.syncQuantityAttributes();
    }
  }

  handleVariantChange() {
    // Update the sticky bar immediately and again after a short delay
    this.updateStickyBarVariant();
    // Re-bind visibility observer in case the main button DOM was replaced
    this.resetVisibilityObserver();
    
    setTimeout(() => {
      this.updateStickyBarVariant();
      this.resetVisibilityObserver();
    }, 50);
  }

  updateStickyBarVariant() {
    const mainForm = document.querySelector('product-form form') ||
                    document.querySelector('form[data-type="add-to-cart-form"]');
    if (!mainForm) return;
    
    const variantIdInput = mainForm.querySelector('[name="id"]');
    if (!variantIdInput) return;
    
    // Update variant ID
    const stickyVariantIdInput = this.querySelector('[name="id"]');
    if (stickyVariantIdInput) {
      stickyVariantIdInput.value = variantIdInput.value;
    }
    
    this.updateVariantTitle();
    this.updateStickyBarPrice();
    this.updateAvailabilityStatus(mainForm);
  }
  
  updateVariantTitle() {
    const stickyFormVariantTitle = this.querySelector('.sticky-add-to-cart__variant');
    if (!stickyFormVariantTitle) return;
    
    // Reset previous content first
    stickyFormVariantTitle.textContent = '';
    
    // Try to get variant display from main product info
    const mainFormVariantTitle = document.querySelector('.product__variant-status');
    
    if (mainFormVariantTitle) {
      stickyFormVariantTitle.textContent = mainFormVariantTitle.textContent;
      return;
    }
    
    // Alternative: gather selected options
    const selectedOptions = [];
    
    // From dropdowns
    document.querySelectorAll('.product-form__input select').forEach(select => {
      if (select.value) {
        selectedOptions.push(select.value);
      }
    });
    
    // From radio buttons
    document.querySelectorAll('.product-form__input input[type="radio"]:checked').forEach(radio => {
      selectedOptions.push(radio.value);
    });
    
    // Display if we have options
    if (selectedOptions.length > 0) {
      const uniqueOptions = [...new Set(selectedOptions)];
      stickyFormVariantTitle.textContent = uniqueOptions.join(' / ');
    }
  }
  
  updateStickyBarPrice() {
    // Target the PDP price wrapper directly by section ID to avoid
    // accidentally selecting the sticky bar's own price element.
    const sectionId = this.dataset.sectionId;
    const mainPrice = document.querySelector(`#price-${sectionId} .price`);
    const stickyPrice = this.querySelector('.sticky-add-to-cart__price');

    if (mainPrice && stickyPrice) {
      stickyPrice.innerHTML = '';
      const priceClone = mainPrice.cloneNode(true);
      stickyPrice.appendChild(priceClone);
    }
  }
  
  updateAvailabilityStatus(mainForm) {
    const addToCartButton = mainForm.querySelector('.product-form__submit');
    const stickyAddToCartButton = this.querySelector('.product-form__submit');
    
    if (addToCartButton && stickyAddToCartButton) {
      // Update button state
      stickyAddToCartButton.disabled = addToCartButton.disabled;
      
      // Update button text
      const mainButtonText = addToCartButton.querySelector('span')?.textContent;
      if (mainButtonText && stickyAddToCartButton.querySelector('span')) {
        stickyAddToCartButton.querySelector('span').textContent = mainButtonText;
      }
    }
  }

  // New: observe main add-to-cart visibility and toggle sticky bar accordingly
  setupVisibilityObserver() {
    // Use observer for both positions; visibility is tied to the main ATC
    const sectionId = this.dataset.sectionId;
    const byIdTarget = sectionId ? document.getElementById(`ProductSubmitButton-${sectionId}`) : null;
    const mainForm = document.querySelector('product-form form') ||
                     document.querySelector('form[data-type="add-to-cart-form"]');
    if (!byIdTarget && !mainForm) {
      this.usesObserver = false;
      return;
    }

    // Fallback: find any product submit button that is not inside this sticky component
    const fallbackTarget = byIdTarget || document.querySelector('button[id^="ProductSubmitButton-"]:not(sticky-add-to-cart button)');

    const mainAddToCartButton = fallbackTarget || mainForm?.querySelector('.product-form__submit');
    const target = mainAddToCartButton || mainForm;

    // If there is still no target, abort
    if (!target) {
      this.usesObserver = false;
      return;
    }

    try {
      this.visibilityObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const isVisible = entry.isIntersecting && entry.intersectionRatio > 0;
        const isShowing = this.classList.contains(this.stickyClass);

        if (isVisible) {
          // Mark that the user has reached the main button at least once
          this.hasReachedMainButton = true;
          if (isShowing) {
            this.classList.remove(this.stickyClass);
          }
          return;
        }

        // Not visible
        if (this.hasReachedMainButton) {
          if (!isShowing) this.classList.add(this.stickyClass);
        } else {
          // Do not show before the user ever reaches the button
          if (isShowing) this.classList.remove(this.stickyClass);
        }
      }, {
        root: null,
        // Treat tiny peeks as visible
        rootMargin: '0px 0px -1px 0px',
        threshold: [0, 0.01, 1]
      });

      this.visibilityObserver.observe(target);
      this.usesObserver = true;
    } catch (e) {
      // Fallback to scroll logic if IntersectionObserver fails
      this.usesObserver = false;
    }
  }

  // New: helper to reset the visibility observer (e.g., after DOM re-render)
  resetVisibilityObserver() {
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    }
    // do not reset hasReachedMainButton to preserve whether user saw the button
    this.setupVisibilityObserver();
  }

  handleScroll() {
    // If observer is active, skip scroll-based logic
    if (this.usesObserver) {
      return;
    }
    const productForm = document.querySelector('product-form') || 
                        document.querySelector('.product-form');
                        
    if (!productForm) return;
    
    const stickyHeight = this.offsetHeight;
    let shouldStick = false;
    
    // Legacy fallback logic (when observer is not available)
    if (this.position === 'top') {
      const header = document.querySelector('#shopify-section-header');
      const headerHeight = header ? header.offsetHeight : 0;
      shouldStick = window.scrollY > headerHeight;
    } else {
      const sectionId = this.dataset.sectionId;
      const atcButton = sectionId ? document.getElementById(`ProductSubmitButton-${sectionId}`) : null;
      if (atcButton) {
        const atcBottom = atcButton.offsetTop + atcButton.offsetHeight;
        shouldStick = window.scrollY > atcBottom;
      } else {
        shouldStick = window.scrollY > (productForm.offsetTop + productForm.offsetHeight);
      }
    }
    
    const isSticky = this.classList.contains(this.stickyClass);
    
    if (shouldStick && !isSticky) {
      this.style.minHeight = `${stickyHeight}px`;
      this.classList.add(this.stickyClass);
    } else if (!shouldStick && isSticky) {
      this.style.removeProperty('min-height');
      this.classList.remove(this.stickyClass);
    }
  }
  
  handleAddToCart(event) {
    if (event) {
      event.preventDefault();
    }
    
    // Find main form
    const mainForm = document.querySelector('product-form form') ||
                    document.querySelector('form[data-type="add-to-cart-form"]');
    
    if (!mainForm) return;
    
    // Sync quantity and variant ID with main form
    this.syncFormData(mainForm);
    
    // Trigger main form submission
    const mainSubmitButton = mainForm.querySelector('[type="submit"]');
    if (mainSubmitButton) {
      mainSubmitButton.click();
    } else {
      mainForm.submit();
    }
  }
  
  syncFormData(mainForm) {
    // Sync quantity
    const stickyQuantityInput = this.querySelector('[data-sticky-quantity]');
    const mainQuantityInput = document.querySelector(`#Quantity-${this.dataset.sectionId}`);
    
    if (stickyQuantityInput && mainQuantityInput) {
      mainQuantityInput.value = stickyQuantityInput.value;
      mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    
    // Sync variant ID
    const stickyVariantIdInput = this.querySelector('[name="id"]');
    const mainVariantIdInput = mainForm.querySelector('[name="id"]');
    
    if (stickyVariantIdInput && mainVariantIdInput) {
      mainVariantIdInput.value = stickyVariantIdInput.value;
    }
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);

// Handle header visibility changes
window.addEventListener("scroll", function() {
  const stickyAddToCart = document.querySelector('sticky-add-to-cart');
  if (!stickyAddToCart) return;
  
  // Handle header state
  const header = document.querySelector('#shopify-section-header');
  const stickyInner = document.querySelector('.sticky-inner-container');
  
  if (header?.classList.contains('shopify-section-header-hidden')) {
    stickyInner?.classList.add('show-my-class');
  } else {
    stickyInner?.classList.remove('show-my-class');
  }
}); 